/**
 * dtrMonthStats.js — the monthly footer buckets, extracted from
 * EmployeeTimeRecords' component-local `monthlyStats` useMemo so the DTR
 * report can share them instead of forking (PRD_DtrReport C14).
 *
 * Shared by the Employee Time Records screen footer (renders Total Days +
 * eight of these, omits Late) and the DTR report footer (renders nine, omits
 * Total Days — PRD_DtrReport C13). Total Days itself stays with the callers:
 * on screen it is the filter-aware FAFS form; on the report it was dropped.
 *
 * All values are raw numbers — callers format (toFixed etc.).
 */
import { hasWorkedRecord, hasFlaggedRecord, LATE_FILTER_TOKEN } from './dayFlags';

/**
 * Compute the day/record buckets for a month of day-grouped records.
 *
 * The four day-type counts are DAY-scoped — one date can legitimately hold
 * several records (split shifts, BL-031). The buckets OVERLAP and are not
 * required to sum to the day count: a date holding both a worked record and a
 * leave record increments Days Worked and Leave, which is correct. Piece Rate
 * is a SUBSET of Days Worked — a piece-rate day is worked, only the pay basis
 * differs (C5). Late is DAY-scoped too, matching the counts beside it
 * (PRD_DtrReport C13). The hour totals are RECORD-scoped sums.
 *
 * @param {Array<{records: Array}>} dayGroups - one entry per date, each with
 *   that date's records (possibly empty)
 * @returns {{daysWorked: number, pieceRateDays: number, leaveDays: number,
 *   absentDays: number, lateDays: number, totalOTHours: number,
 *   totalNightDiff: number, totalUtHours: number, totalRegularHours: number,
 *   totalBreakMinutes: number}}
 */
export const computeMonthStats = (dayGroups) => {
  let daysWorked = 0;
  let pieceRateDays = 0;
  let leaveDays = 0;
  let absentDays = 0;
  let lateDays = 0;
  let totalOTHours = 0;
  let totalNightDiff = 0;
  let totalUtHours = 0;
  let totalRegularHours = 0;
  // Record-scoped like the hour sums; the Attendance Summary report renders it
  // in hours (PRD_AttendanceSummaryReport C7). Rendered by neither the ETR
  // footer nor the DTR totals band.
  let totalBreakMinutes = 0;

  (dayGroups || []).forEach(({ records }) => {
    if (records.length > 0) {
      if (hasWorkedRecord(records)) daysWorked++;
      if (hasFlaggedRecord(records, 'piece rate')) pieceRateDays++;
      if (hasFlaggedRecord(records, 'leave')) leaveDays++;
      if (hasFlaggedRecord(records, 'absent')) absentDays++;
      if (hasFlaggedRecord(records, LATE_FILTER_TOKEN)) lateDays++;
      records.forEach((record) => {
        totalOTHours += record.overtimeHours || 0;
        totalNightDiff += record.nightDiffHours || 0;
        totalUtHours += record.undertimeHours || 0;
        totalRegularHours += record.regularHours || 0;
        totalBreakMinutes += record.breakMinutes || 0;
      });
    }
  });

  return {
    daysWorked,
    pieceRateDays,
    leaveDays,
    absentDays,
    lateDays,
    totalOTHours,
    totalNightDiff,
    totalUtHours,
    totalRegularHours,
    totalBreakMinutes,
  };
};
